import { motion } from 'framer-motion';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/badge';
import { Code, User, Building, Layers, Palette, Rocket, Shield, Cloud, Zap } from 'lucide-react';

const expertiseAreas = [
  {
    id: 1,
    title: 'Création Web Pro',
    description: 'Sites vitrines, e-commerce et applications web sur mesure avec les dernières technologies.',
    icon: Code,
    features: ['Sites Vitrines', 'E-commerce', 'Web Apps', 'SEO Optimisé'],
    gradient: 'from-[#00f2fe] to-[#4facfe]',
    glow: 'rgba(79,172,254,0.25)',
  },
  {
    id: 2,
    title: 'Services Particuliers',
    description: 'Solutions digitales personnalisées pour freelances, artisans et entrepreneurs individuels.',
    icon: User,
    features: ['Portfolio', 'Blog Personnel', 'Landing Pages', 'Identité Visuelle'],
    gradient: 'from-[#4facfe] to-[#a855f7]',
    glow: 'rgba(168,85,247,0.20)',
  },
  {
    id: 3,
    title: 'Solutions Entreprises',
    description: 'Infrastructures robustes et scalables pour PME et grandes entreprises.',
    icon: Building,
    features: ['SaaS', 'ERP/CRM', 'Intranet', 'API & Intégrations'],
    gradient: 'from-[#a855f7] to-[#ec4899]',
    glow: 'rgba(236,72,153,0.18)',
  },
];

const additionalServices = [
  { icon: Layers, label: 'Architecture Modulaire' },
  { icon: Palette, label: 'Design UI/UX' },
  { icon: Rocket, label: 'Performance Optimale' },
  { icon: Shield, label: 'Sécurité Avancée' },
  { icon: Cloud, label: 'Cloud Native' },
  { icon: Zap, label: 'Temps Réel' },
];

export const Expertise = () => {
  return (
    <section
      id="expertise"
      data-testid="expertise-section"
      className="relative py-24 sm:py-36"
    >
      {/* Section ambient glow — violet (stronger) */}
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 65%)',
          filter: 'blur(60px)',
        }}
      />
      {/* Section ambient glow — cyan left */}
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[400px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(79,172,254,0.08) 0%, transparent 65%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="inline-block text-[#a855f7] text-xs font-semibold tracking-[0.2em] uppercase mb-4">
            Ce que nous faisons
          </span>
          <h2 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-5">
            Notre <span className="text-gradient">Expertise</span>
          </h2>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Des solutions adaptées à chaque besoin, de la startup au grand groupe.
          </p>
          <div className="section-glow-divider max-w-xs mx-auto mt-8" />
        </motion.div>

        {/* Main Expertise Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-14">
          {expertiseAreas.map((area, index) => (
            <GlassCard
              key={area.id}
              data-testid={`expertise-card-${area.id}`}
              delay={index * 0.15}
              className="text-center relative group"
            >
              {/* Icon with Glow */}
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${area.gradient} p-[1.5px]`}
                style={{ boxShadow: `0 0 24px ${area.glow}` }}
              >
                <div className="w-full h-full rounded-2xl bg-[#060c1e] flex items-center justify-center">
                  <area.icon className="w-8 h-8 text-white" />
                </div>
              </motion.div>

              {/* Title */}
              <h3 className="font-outfit text-xl sm:text-2xl font-bold text-white mb-3">
                {area.title}
              </h3>

              {/* Description */}
              <p className="text-white/72 text-sm leading-relaxed mb-6">
                {area.description}
              </p>

              {/* Features — shadcn Badge pills */}
              <div className="flex flex-wrap gap-2 justify-center">
                {area.features.map((feature) => (
                  <Badge
                    key={feature}
                    variant="outline"
                    className={`rounded-full text-xs font-medium border-white/10 text-white/65 bg-white/[0.04] px-3 py-1 hover:text-white/90 hover:bg-white/[0.07] transition-[background-color,color] duration-200`}
                  >
                    {feature}
                  </Badge>
                ))}
              </div>

              {/* Hover Glow Effect */}
              <div
                className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${area.gradient} opacity-0 group-hover:opacity-[0.07] transition-opacity duration-500 pointer-events-none`}
              />
            </GlassCard>
          ))}
        </div>

        {/* Additional Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <GlassCard hover={false} className="p-8 sm:p-12">
            <h3 className="font-outfit text-xl sm:text-2xl font-bold text-white text-center mb-10">
              Technologies & Méthodologies
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
              {additionalServices.map((service, index) => (
                <motion.div
                  key={service.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -6, scale: 1.05 }}
                  className="flex flex-col items-center gap-3 group cursor-default"
                >
                  <div className="w-13 h-13 w-[52px] h-[52px] rounded-xl bg-[#4facfe]/[0.07] border border-[#4facfe]/15 flex items-center justify-center group-hover:border-[#4facfe]/40 group-hover:bg-[#4facfe]/[0.12] group-hover:shadow-[0_0_18px_rgba(79,172,254,0.25)] transition-all duration-300">
                    <service.icon className="w-6 h-6 text-[#4facfe]" />
                  </div>
                  <span className="text-white/72 text-xs font-medium text-center group-hover:text-white/90 transition-colors">
                    {service.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </GlassCard>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-white/70 mb-6 text-base">
            Prêt à donner vie à votre projet ?
          </p>
          <motion.button
            data-testid="cta-expertise-button"
            className="bg-gradient-to-r from-[#00f2fe] to-[#4facfe] text-[#030a1a] font-semibold rounded-xl px-9 py-4 text-base transition-all animate-glow-pulse"
            whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(79, 172, 254, 0.55)' }}
            whileTap={{ scale: 0.97 }}
          >
            Discutons de votre projet
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Expertise;
